if (!customElements.get('mm-gallery-fill')) {
  customElements.define('mm-gallery-fill', class MMGalleryFill extends HTMLElement {
    static planCount({mediaBottom, gridTop, rowHeight, gap, columns, initial, maximum}) {
      if (!Number.isFinite(mediaBottom) || !Number.isFinite(gridTop) || !(rowHeight > 0)) return initial;
      const rows = Math.floor((Math.max(0, mediaBottom - gridTop) + gap) / (rowHeight + gap));
      return Math.max(initial, Math.min(maximum, rows * columns));
    }

    connectedCallback() {
      const product = this.closest('product-info');
      const recommendations = this.parentElement.querySelector('product-recommendations.related-products');
      if (!product?.closest('main') || this.closest('quick-add-modal') || !recommendations || !this.dataset.collectionUrl) return;
      const identity = `${product.dataset.url}|${this.dataset.collectionUrl}`;
      // Desktop/mobile moves the same discovery subtree synchronously. Keep its
      // cursor, card nodes and request alive instead of treating that as a new PDP.
      if (this.active && this.product === product && this.recommendations === recommendations && this.feedIdentity === identity) {
        this.schedule();
        return;
      }
      if (this.active) this.stopObserving();
      this.product = product;
      this.recommendations = recommendations;
      this.maximum = Math.min(96, Math.max(12, Number(this.dataset.maximum) || 64));
      this.desktop = window.matchMedia('(min-width: 750px)');
      this.token = {};
      if (this.feedIdentity !== identity) {
        this.resetFeed();
        this.feedIdentity = identity;
      } else {
        this.abort = new AbortController();
        this.loading = false;
      }
      this.active = true;
      this.schedule = () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          if (this.isConnected) this.reconcile();
        });
      };
      this.mutations = new MutationObserver(this.schedule);
      this.mutations.observe(this.product, {childList: true, subtree: true});
      this.resize = typeof ResizeObserver === 'function' ? new ResizeObserver(this.schedule) : null;
      this.observed = new WeakSet();
      this.desktop.addEventListener('change', this.schedule);
      window.addEventListener('resize', this.schedule, {passive: true});
      window.addEventListener('scroll', this.schedule, {passive: true});
      this.product.addEventListener('load', this.schedule, true);
      document.addEventListener('focusin', this.schedule);
      document.addEventListener('focusout', this.schedule);
      this.schedule();
    }

    disconnectedCallback() {
      // append()/insertBefore() invokes disconnect/connect even when the same
      // element stays on this page. Only dispose after a genuine removal.
      queueMicrotask(() => { if (!this.isConnected) this.stopObserving(); });
    }

    stopObserving() {
      this.active = false;
      this.token = {};
      this.abort?.abort();
      this.loading = false;
      this.mutations?.disconnect();
      this.resize?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.desktop?.removeEventListener('change', this.schedule);
      window.removeEventListener('resize', this.schedule);
      window.removeEventListener('scroll', this.schedule);
      this.product?.removeEventListener('load', this.schedule, true);
      document.removeEventListener('focusin', this.schedule);
      document.removeEventListener('focusout', this.schedule);
    }

    resetFeed() {
      this.abort?.abort();
      this.abort = new AbortController();
      this.generation = (this.generation || 0) + 1;
      this.requests = 0;
      this.loading = false;
      this.stopped = false;
      this.pool = [];
      this.candidates = new Map();
      this.nextUrl = this.feedUrl(this.dataset.collectionUrl);
    }

    restoreGrid(grid) {
      this.grid = grid;
      this.rowHeight = 0;
      this.width = 0;
      const seen = new Set([...grid.children].map(item => this.productKey(item.querySelector('.mm-basket-card__media')?.href || '')));
      const baseByKey = new Map([...grid.children].map(item => [this.productKey(item.querySelector('.mm-basket-card__media')?.href || ''), item]));
      seen.add(this.productKey(this.product.dataset.url));
      // The native response owns its base cards. Reattach previously added nodes
      // beside that fresh base, retaining form IDs and modal opener identity.
      for (const [key, item] of this.candidates) {
        if (!item.hasAttribute('data-mm-gallery-extra') && !this.protectedItem(item)) continue;
        if (seen.has(key)) {
          // If a native base now includes the open modal's product, keep its
          // original opener node as that base card instead of duplicating it.
          const base = baseByKey.get(key);
          if (base && base !== item && this.protectedItem(item)) {
            item.removeAttribute('data-mm-gallery-extra');
            grid.replaceChild(item, base);
          }
          continue;
        }
        if (grid.children.length >= this.maximum) continue;
        grid.append(item);
        seen.add(key);
      }
      this.pool = [...this.candidates].filter(([key, item]) => !seen.has(key) && !item.hasAttribute('data-mm-gallery-extra')).map(([, item]) => item);
    }

    feedUrl(value) {
      const url = new URL(value, new URL(this.dataset.collectionUrl, window.location.origin));
      if (url.origin !== window.location.origin) return null;
      url.searchParams.set('section_id', 'mm-gallery-discovery-feed');
      url.searchParams.set('sort_by', 'price-ascending');
      const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      return url.toString();
    }

    productKey(value) {
      return new URL(value, window.location.origin).pathname.match(/\/products\/([^/]+)/)?.[1] || '';
    }

    protectedItem(item) {
      const modal = document.querySelector('quick-add-modal[open]');
      const opener = modal?.resolveOpener?.() || modal?.openedBy;
      return item.contains(document.activeElement) || (opener && item.contains(opener));
    }

    observeSize(element) {
      if (element && this.resize && !this.observed.has(element)) {
        this.observed.add(element);
        this.resize.observe(element);
      }
    }

    reconcile() {
      const grid = this.recommendations.querySelector('ul.product-grid');
      if (!grid) return;
      if (this.grid !== grid) this.restoreGrid(grid);
      const items = [...grid.children].filter(item => item.matches('li.grid__item'));
      const initial = items.filter(item => !item.hasAttribute('data-mm-gallery-extra')).length;
      const extras = items.filter(item => item.hasAttribute('data-mm-gallery-extra'));
      if (!this.desktop.matches) {
        extras.forEach(item => { item.hidden = !this.protectedItem(item); });
        return;
      }
      const details = this.product.querySelector('.mm-product-details');
      const lastMedia = details?.querySelector('ul > li:last-child') || this.product.querySelector('media-gallery');
      this.observeSize(details);
      this.observeSize(this.product.querySelector('media-gallery'));
      this.observeSize(this.product.querySelector('.product__info-container'));
      const width = grid.getBoundingClientRect().width;
      const measuredHeight = Math.max(0, ...items.filter(item => !item.hidden).map(item => item.getBoundingClientRect().height));
      if (Math.abs((this.width || 0) - width) > 1) this.rowHeight = 0;
      this.width = width;
      // Keep the tallest observed row at this width; hiding one cannot start a size oscillation.
      const rowHeight = this.rowHeight = Math.max(this.rowHeight || 0, measuredHeight);
      const css = getComputedStyle(grid);
      const gap = parseFloat(css.rowGap) || 0;
      const columns = css.gridTemplateColumns.split(/\s+/).filter(Boolean).length || 2;
      // The last actual media item is independent of any stretched grid-column box.
      const desired = MMGalleryFill.planCount({
        mediaBottom: lastMedia?.getBoundingClientRect().bottom,
        gridTop: grid.getBoundingClientRect().top,
        rowHeight, gap, columns, initial, maximum: this.maximum
      });
      extras.forEach((item, index) => { item.hidden = initial + index >= desired && !this.protectedItem(item); });
      const visible = [...grid.children].filter(item => !item.hidden && item.matches('li.grid__item'));
      const last = visible.at(-1);
      if (visible.length >= desired || !last || last.getBoundingClientRect().bottom > window.innerHeight + 650) return;
      const seen = new Set(items.map(item => this.productKey(item.querySelector('.mm-basket-card__media')?.href || '')));
      seen.add(this.productKey(this.product.dataset.url));
      let appended = 0;
      while (this.pool.length && visible.length + appended < desired && appended < 12 && items.length + appended < this.maximum) {
        const item = this.pool.shift();
        const key = this.productKey(item.querySelector('.mm-basket-card__media')?.href || '');
        if (!key || seen.has(key)) continue;
        seen.add(key);
        item.setAttribute('data-mm-gallery-extra', '');
        grid.append(item);
        appended++;
      }
      if (appended) {
        this.schedule();
        return;
      }
      if (!this.loading && !this.stopped && this.nextUrl && this.requests < 8 && items.length < this.maximum) this.loadNext();
    }

    async loadNext() {
      this.loading = true;
      this.requests++;
      const token = this.token;
      const generation = this.generation;
      try {
        const response = await fetch(this.nextUrl, {signal: this.abort.signal});
        if (!response.ok) throw new Error('Recommendation page unavailable');
        const html = await response.text();
        if (token !== this.token || generation !== this.generation || !this.isConnected) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const feed = parsed.querySelector('template[data-mm-gallery-feed]');
        if (!feed) throw new Error('Recommendation feed unavailable');
        const current = this.productKey(this.product.dataset.url);
        for (const item of feed.content.querySelectorAll('ul > li.grid__item')) {
          const key = this.productKey(item.querySelector('.mm-basket-card__media')?.href || '');
          if (!key || key === current || this.candidates.has(key)) continue;
          this.candidates.set(key, item);
          this.pool.push(item);
        }
        this.nextUrl = feed.dataset.nextUrl ? this.feedUrl(feed.dataset.nextUrl) : null;
        if (!this.nextUrl) this.stopped = true;
      } catch (error) {
        if (token === this.token && generation === this.generation && error.name !== 'AbortError') this.stopped = true;
      } finally {
        if (token === this.token && generation === this.generation) {
          this.loading = false;
          this.schedule();
        }
      }
    }
  });
}
